import * as FileSystem from "@/platform/file-system";

/**
 * Caminho de arquivo guardado ANTES de uma atualização do app, trazido para a pasta ATUAL.
 *
 * Por que existe (06/10/2026, "atualizei o app e perdi as batidas offline"): no iOS a pasta
 * do app (`.../Containers/Data/Application/<UUID>/Documents/`) MUDA de UUID a cada
 * atualização pela loja — os arquivos vêm junto, o caminho antigo deixa de existir. O app
 * guardava o caminho ABSOLUTO das fotos e anexos (batida, O.S., livro, checklist,
 * justificativa, diária) na fila, no mapa de pontos e nos espelhos. Depois do update:
 *  - a batida pendente subia SEM foto (o envio via o arquivo "inexistente");
 *  - ações que exigem o arquivo (livro, por exemplo) ficavam presas na fila para sempre.
 * O próprio app já sabia disso para a foto do cadastro facial (FaceRecognition guarda só o
 * nome); o resto não.
 *
 * Aqui o prefixo antigo do container (`.../Application/<UUID>/`, antes de `Documents/`,
 * `Library/` ou `tmp/`) vira o atual — o resto do caminho é o mesmo, porque o conteúdo é
 * migrado. No Android o caminho não muda (`/data/user/0/<pacote>/files/`) e nada é
 * reescrito (sem a raiz do iOS, as funções devolvem o valor como veio).
 */

// WEB: o `documentDirectory` do VFS (`file:///tirvu/...`) não é container do iOS — `RAIZ_ATUAL`
// fica vazia e as duas funções devolvem o valor como veio (identidade), como no Android.
const DOC_DIR_ATUAL: string = (FileSystem as any).documentDirectory ?? "";
/** Raiz do container atual (`.../Application/<UUID>/`), quando a pasta de documentos é a do iOS. */
const RAIZ_ATUAL =
  /\/Containers\/Data\/Application\/[0-9A-Fa-f-]{36}\/Documents\/$/.test(
    DOC_DIR_ATUAL,
  )
    ? DOC_DIR_ATUAL.replace(/Documents\/$/, "")
    : "";

/**
 * `.../Containers/Data/Application/<UUID>/` seguido de `Documents/`, `Library/` ou `tmp/` —
 * iPhone e simulador. `Library/` também vem junto no update (o fallback que guarda o caminho
 * do cache da câmera quando a cópia falhou).
 */
const RE_CONTAINER_IOS =
  /^(?:file:\/\/)?(?:\/private)?\/(?:[^?#]*?\/)?Containers\/Data\/Application\/[0-9A-Fa-f-]{36}\/(?=(?:Documents|Library|tmp)\/)/;

/**
 * O caminho na pasta ATUAL do app. Só mexe em caminho do container de OUTRA instalação do
 * iOS; qualquer outra coisa (URL, base64, caminho já atual, Android) volta igual.
 */
export function caminhoNoAppAtual<T>(valor: T): T {
  if (typeof valor !== "string" || !RAIZ_ATUAL) return valor;
  if (valor.startsWith(RAIZ_ATUAL)) return valor;
  // Barato: só strings que parecem caminho de arquivo passam pela regex.
  if (!valor.includes("/Containers/Data/Application/")) return valor;
  const m = valor.match(RE_CONTAINER_IOS);
  if (!m) return valor;
  return (RAIZ_ATUAL + valor.slice(m[0].length)) as unknown as T;
}

/**
 * Reescreve, NO LUGAR, todo caminho de outra instalação dentro de um valor recém-lido do
 * armazenamento (objeto/array vindos de JSON.parse). Devolve o mesmo valor. Só para dado
 * recém-parseado: nada aqui é compartilhado com quem já estava usando o objeto.
 */
export function reescreverCaminhosAntigos<T>(valor: T): T {
  if (!RAIZ_ATUAL) return valor;
  const visitar = (v: any): any => {
    if (typeof v === "string") return caminhoNoAppAtual(v);
    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) v[i] = visitar(v[i]);
      return v;
    }
    if (v && typeof v === "object") {
      for (const k of Object.keys(v)) v[k] = visitar(v[k]);
      return v;
    }
    return v;
  };
  return visitar(valor);
}
